import { Injectable }              from '@angular/core';
import {
	TssAssignmentTypeEnum,
	TssDeviceTypeEnum,
	TssExternalStatusEnum,
	TssModeEnum,
	TssModeSubTypeEnum,
	TssModeWithSubTypeEnum,
	TssStatusEnum
}                                  from '@app/modules/tss/models/tss.model';
import { VirtualTssStatusEnumDTO } from '@openApiGenerated/admin-api';
import {
	AssignmentTypeEnumDTO,
	ExtVirtualTssStatusEnumDTO,
	VirtualTssDeviceTypeEnumDTO,
	VirtualTssModeEnumDTO,
	VirtualTssModeSubtypeEnumDTO,
	VirtualTssModeWithSubtypeEnumDTO
}                                  from '@openApiGenerated/management-api';
import { LoggerService }           from '@shared/services/logger.service';

@Injectable( {
	providedIn: 'root'
} )
export class TssMapperService {

	constructor(
		private _logger: LoggerService
	) {}

	get tss() {
		return {
			assignmentTypeDtoToTmo: this.mapAssignmentTypeDtoToTmo.bind( this ) as typeof this.mapAssignmentTypeDtoToTmo,
			assignmentTypeTmoToDto: this.mapAssignmentTypeTmoToDto.bind( this ) as typeof this.mapAssignmentTypeTmoToDto,
			deviceTypeDtoToTmo: this.mapDeviceTypeDtoToTmo.bind( this ) as typeof this.mapDeviceTypeDtoToTmo,
			deviceTypeTmoToDto: this.mapDeviceTypeTmoToDto.bind( this ) as typeof this.mapDeviceTypeTmoToDto,
			modeAndSubtypeDtoToTmo: this.mapTssModeAndSubtypeDtoToTmo.bind( this ) as typeof this.mapTssModeAndSubtypeDtoToTmo,
			modeAndSubtypeTmoToDto: this.mapTssModeAndSubtypeTmoToDto.bind( this ) as typeof this.mapTssModeAndSubtypeTmoToDto,
			modeDtoToTmo: this.mapTssModeDtoToTmo.bind( this ) as typeof this.mapTssModeDtoToTmo,
			modeTmoToDto: this.mapTssModeTmoToDto.bind( this ) as typeof this.mapTssModeTmoToDto,
			modeSubtypeDtoToTmo: this.mapTssModeSubtypeDtoToTmo.bind( this ) as typeof this.mapTssModeSubtypeDtoToTmo,
			modeSubtypeTmoToDto: this.mapTssModeSubtypeTmoToDto.bind( this ) as typeof this.mapTssModeSubtypeTmoToDto,
			modeWithSubtypeDtoToTmo: this.mapTssModeWithSubtypeDtoToTmo.bind( this ) as typeof this.mapTssModeWithSubtypeDtoToTmo,
			modeWithSubtypeTmoToDto: this.mapTssModeWithSubtypeTmoToDto.bind( this ) as typeof this.mapTssModeWithSubtypeTmoToDto,
			statusDtoToTmo: this.mapStatusDtoToTmo.bind( this ) as typeof this.mapStatusDtoToTmo,
			statusTmoToDto: this.mapStatusTmoToDto.bind( this ) as typeof this.mapStatusTmoToDto,
			statusListTmoToDto: this.mapStatusListTmoToDto.bind( this ) as typeof this.mapStatusListTmoToDto,
			statusExternalDtoToTmo: this.mapStatusExternalDtoToTmo.bind( this ) as typeof this.mapStatusExternalDtoToTmo,
			statusExternalTmoToDto: this.mapStatusExternalTmoToDto.bind( this ) as typeof this.mapStatusExternalTmoToDto,
			statusListExternalTmoToDto: this.mapStatusListExternalTmoToDto.bind( this ) as typeof this.mapStatusListExternalTmoToDto
		};
	}

	private mapAssignmentTypeDtoToTmo( assignmentType: AssignmentTypeEnumDTO ): TssAssignmentTypeEnum {

		switch ( assignmentType ) {
			case AssignmentTypeEnumDTO.BUSINESS_PREMISES:
				return TssAssignmentTypeEnum.BUSINESS_PREMISES;

			case AssignmentTypeEnumDTO.ERS:
				return TssAssignmentTypeEnum.ERS;

			default:
				if ( assignmentType ) {
					this._logger.warn( `mapAssignmentTypeDtoToTmo has no case for TMO ${ assignmentType }` );
				}
				return null;
		}
	}

	private mapAssignmentTypeTmoToDto( assignmentType: TssAssignmentTypeEnum ): AssignmentTypeEnumDTO {

		switch ( assignmentType ) {
			case TssAssignmentTypeEnum.BUSINESS_PREMISES:
				return AssignmentTypeEnumDTO.BUSINESS_PREMISES;

			case TssAssignmentTypeEnum.ERS:
				return AssignmentTypeEnumDTO.ERS;

			default:
				if ( assignmentType ) {
					this._logger.warn( `mapAssignmentTypeTmoToDto has no case for DTO ${ assignmentType }` );
				}
				return null;
		}
	}

	private mapDeviceTypeDtoToTmo( deviceType: VirtualTssDeviceTypeEnumDTO ): TssDeviceTypeEnum {

		switch ( deviceType ) {
			case VirtualTssDeviceTypeEnumDTO.CLOUD:
				return TssDeviceTypeEnum.CLOUD;

			case VirtualTssDeviceTypeEnumDTO.SD:
				return TssDeviceTypeEnum.SD;

			case VirtualTssDeviceTypeEnumDTO.USB:
				return TssDeviceTypeEnum.USB;

			case VirtualTssDeviceTypeEnumDTO.USD:
				return TssDeviceTypeEnum.MICRO_SD;

			default:
				if ( deviceType ) {
					this._logger.warn( `mapDeviceTypeDtoToTmo has no case for TMO ${ deviceType }` );
				}
				return null;
		}
	}

	private mapDeviceTypeTmoToDto( deviceType: TssDeviceTypeEnum ): VirtualTssDeviceTypeEnumDTO {

		switch ( deviceType ) {
			case TssDeviceTypeEnum.CLOUD:
				return VirtualTssDeviceTypeEnumDTO.CLOUD;

			case TssDeviceTypeEnum.MICRO_SD:

				return VirtualTssDeviceTypeEnumDTO.USD;

			case TssDeviceTypeEnum.SD:
				return VirtualTssDeviceTypeEnumDTO.SD;

			case TssDeviceTypeEnum.USB:
				return VirtualTssDeviceTypeEnumDTO.USB;

			default:
				if ( deviceType ) {
					this._logger.warn( `mapDeviceTypeTmoToDto has no case for TMO ${ deviceType }` );
				}
				return null;
		}
	}

	private mapTssModeAndSubtypeDtoToTmo( tssMode: VirtualTssModeEnumDTO, tssModeSubType: VirtualTssModeSubtypeEnumDTO ): TssModeWithSubTypeEnum {
		switch ( tssMode ) {
			case VirtualTssModeEnumDTO.CLOUD:
				return TssModeWithSubTypeEnum.CLOUD;

			case VirtualTssModeEnumDTO.OFFLINE:
				return TssModeWithSubTypeEnum.OFFLINE;

			case VirtualTssModeEnumDTO.TOKEN:
				switch ( tssModeSubType ) {
					case VirtualTssModeSubtypeEnumDTO.CV1:
						return TssModeWithSubTypeEnum.TOKEN_CV1;

					case VirtualTssModeSubtypeEnumDTO.CV2:
						return TssModeWithSubTypeEnum.TOKEN_CV2;
				}
				break;

			case VirtualTssModeEnumDTO.SWISSBIT_TOKEN:
				return TssModeWithSubTypeEnum.SWISSBIT_TOKEN;

			default:
				return null;
		}
	}

	private mapTssModeAndSubtypeTmoToDto( tssMode: TssModeEnum, tssModeSubType: TssModeSubTypeEnum ): VirtualTssModeWithSubtypeEnumDTO {
		switch ( tssMode ) {
			case TssModeEnum.CLOUD:
				return VirtualTssModeWithSubtypeEnumDTO.CLOUD;

			case TssModeEnum.OFFLINE:
				return VirtualTssModeWithSubtypeEnumDTO.OFFLINE;

			case TssModeEnum.TOKEN:
				switch ( tssModeSubType ) {
					case TssModeSubTypeEnum.CV1:
						return VirtualTssModeWithSubtypeEnumDTO.TOKEN_CV1;

					case TssModeSubTypeEnum.CV2:
						return VirtualTssModeWithSubtypeEnumDTO.TOKEN_CV2;
				}
				break;

			case TssModeEnum.SWISSBIT_TOKEN:
				return VirtualTssModeWithSubtypeEnumDTO.SWISSBIT_TOKEN;

			default:
				return null;
		}
	}

	private mapTssModeDtoToTmo( tssMode: VirtualTssModeEnumDTO ): TssModeEnum {
		switch ( tssMode ) {
			case VirtualTssModeEnumDTO.CLOUD:
				return TssModeEnum.CLOUD;

			case VirtualTssModeEnumDTO.OFFLINE:
				return TssModeEnum.OFFLINE;

			case VirtualTssModeEnumDTO.TOKEN:
				return TssModeEnum.TOKEN;

			case VirtualTssModeEnumDTO.SWISSBIT_TOKEN:
				return TssModeEnum.SWISSBIT_TOKEN;

			default:
				return null;
		}
	}

	private mapTssModeTmoToDto( tssMode: TssModeEnum ): VirtualTssModeEnumDTO {
		switch ( tssMode ) {
			case TssModeEnum.CLOUD:
				return VirtualTssModeEnumDTO.CLOUD;

			case TssModeEnum.OFFLINE:
				return VirtualTssModeEnumDTO.OFFLINE;

			case TssModeEnum.TOKEN:
				return VirtualTssModeEnumDTO.TOKEN;

			case TssModeEnum.SWISSBIT_TOKEN:
				return VirtualTssModeEnumDTO.SWISSBIT_TOKEN;

			default:
				return null;
		}
	}

	private mapTssModeSubtypeTmoToDto( tssModeSubType: TssModeSubTypeEnum ): VirtualTssModeSubtypeEnumDTO {
		switch ( tssModeSubType ) {
			case TssModeSubTypeEnum.CV1:
				return VirtualTssModeSubtypeEnumDTO.CV1;

			case TssModeSubTypeEnum.CV2:
				return VirtualTssModeSubtypeEnumDTO.CV2;

			case TssModeSubTypeEnum.NO_SUBTYPE:
				return null;
		}
	}

	private mapTssModeSubtypeDtoToTmo( tssModeSubTypeDto: VirtualTssModeSubtypeEnumDTO ): TssModeSubTypeEnum {
		switch ( tssModeSubTypeDto ) {
			case VirtualTssModeSubtypeEnumDTO.CV1:
				return TssModeSubTypeEnum.CV1;

			case VirtualTssModeSubtypeEnumDTO.CV2:
				return TssModeSubTypeEnum.CV2;

			default:
				return TssModeSubTypeEnum.NO_SUBTYPE;
		}
	}

	private mapTssModeWithSubtypeDtoToTmo( tssModeWithSubtype: VirtualTssModeWithSubtypeEnumDTO ): TssModeWithSubTypeEnum {
		switch ( tssModeWithSubtype ) {
			case VirtualTssModeWithSubtypeEnumDTO.CLOUD:
				return TssModeWithSubTypeEnum.CLOUD;

			case VirtualTssModeWithSubtypeEnumDTO.OFFLINE:
				return TssModeWithSubTypeEnum.OFFLINE;

			case VirtualTssModeWithSubtypeEnumDTO.TOKEN:
				return TssModeWithSubTypeEnum.TOKEN;

			case VirtualTssModeWithSubtypeEnumDTO.TOKEN_CV1:
				return TssModeWithSubTypeEnum.TOKEN_CV1;

			case VirtualTssModeWithSubtypeEnumDTO.TOKEN_CV2:
				return TssModeWithSubTypeEnum.TOKEN_CV2;

			case VirtualTssModeWithSubtypeEnumDTO.SWISSBIT_TOKEN:
				return TssModeWithSubTypeEnum.SWISSBIT_TOKEN;

			default:
				return null;
		}
	}

	private mapTssModeWithSubtypeTmoToDto( tssModeWithSubtype: TssModeWithSubTypeEnum ): VirtualTssModeWithSubtypeEnumDTO {
		switch ( tssModeWithSubtype ) {
			case TssModeWithSubTypeEnum.CLOUD:
				return VirtualTssModeWithSubtypeEnumDTO.CLOUD;

			case TssModeWithSubTypeEnum.OFFLINE:
				return VirtualTssModeWithSubtypeEnumDTO.OFFLINE;

			case TssModeWithSubTypeEnum.TOKEN:
				return VirtualTssModeWithSubtypeEnumDTO.TOKEN;

			case TssModeWithSubTypeEnum.TOKEN_CV1:
				return VirtualTssModeWithSubtypeEnumDTO.TOKEN_CV1;

			case TssModeWithSubTypeEnum.TOKEN_CV2:
				return VirtualTssModeWithSubtypeEnumDTO.TOKEN_CV2;

			case TssModeWithSubTypeEnum.SWISSBIT_TOKEN:
				return VirtualTssModeWithSubtypeEnumDTO.SWISSBIT_TOKEN;

			default:
				return null;
		}
	}

	private mapStatusDtoToTmo( status: VirtualTssStatusEnumDTO ): TssStatusEnum {
		switch ( status ) {
			case VirtualTssStatusEnumDTO.ACTIVE:
				return TssStatusEnum.ACTIVE;

			case VirtualTssStatusEnumDTO.DELETED:
				return TssStatusEnum.DELETED;

			case VirtualTssStatusEnumDTO.DISABLED:
				return TssStatusEnum.DISABLED;

			case VirtualTssStatusEnumDTO.INACTIVE:
				return TssStatusEnum.INACTIVE;

			case VirtualTssStatusEnumDTO.SUSPENDED:
				return TssStatusEnum.SUSPENDED;

			default:
				return null;
		}
	}

	private mapStatusTmoToDto( status: TssStatusEnum ): VirtualTssStatusEnumDTO {
		switch ( status ) {
			case TssStatusEnum.ACTIVE:
				return VirtualTssStatusEnumDTO.ACTIVE;

			case TssStatusEnum.DELETED:
				return VirtualTssStatusEnumDTO.DELETED;

			case TssStatusEnum.DISABLED:
				return VirtualTssStatusEnumDTO.DISABLED;

			case TssStatusEnum.INACTIVE:
				return VirtualTssStatusEnumDTO.INACTIVE;

			case TssStatusEnum.SUSPENDED:
				return VirtualTssStatusEnumDTO.SUSPENDED;

			default:
				return null;
		}
	}

	private mapStatusListTmoToDto( statusList: TssStatusEnum[] ): VirtualTssStatusEnumDTO[] {
		const dtoStatusList: VirtualTssStatusEnumDTO[] = statusList?.map( this.mapStatusTmoToDto );
		return dtoStatusList;
	}

	private mapStatusExternalDtoToTmo( status: ExtVirtualTssStatusEnumDTO ): TssExternalStatusEnum {
		switch ( status ) {
			case ExtVirtualTssStatusEnumDTO.ACTIVE:
				return TssExternalStatusEnum.ACTIVE;

			case ExtVirtualTssStatusEnumDTO.DELETED:
				return TssExternalStatusEnum.DELETED;

			case ExtVirtualTssStatusEnumDTO.DISABLED:
				return TssExternalStatusEnum.DISABLED;

			case ExtVirtualTssStatusEnumDTO.INACTIVE:
				return TssExternalStatusEnum.INACTIVE;

			default:
				return null;
		}
	}

	private mapStatusExternalTmoToDto( status: TssExternalStatusEnum ): ExtVirtualTssStatusEnumDTO {
		switch ( status ) {
			case TssExternalStatusEnum.ACTIVE:
				return ExtVirtualTssStatusEnumDTO.ACTIVE;

			case TssExternalStatusEnum.DELETED:
				return ExtVirtualTssStatusEnumDTO.DELETED;

			case TssExternalStatusEnum.DISABLED:
				return ExtVirtualTssStatusEnumDTO.DISABLED;

			case TssExternalStatusEnum.INACTIVE:
				return ExtVirtualTssStatusEnumDTO.INACTIVE;

			default:
				return null;
		}
	}

	private mapStatusListExternalTmoToDto( statusList: TssExternalStatusEnum[] ): ExtVirtualTssStatusEnumDTO[] {
		const dtoStatusList: ExtVirtualTssStatusEnumDTO[] = statusList?.map( this.mapStatusExternalTmoToDto );
		return dtoStatusList;
	}
}
